(function () {
  var captureInProgress = false;
  var cartMutationQueue = Promise.resolve();

  function oneValue(params, name) {
    var values = params.getAll(name);
    return values.length === 1 ? values[0] : null;
  }

  function isValidGoogleClick(value) {
    return typeof value === "string" && /^[A-Za-z0-9_-]{1,256}$/.test(value);
  }

  function sameIdentifiers(current, candidate) {
    if (!candidate || Object.keys(current).length !== Object.keys(candidate).length) return false;
    return ["gclid", "gbraid", "wbraid"].every(function (name) {
      return current[name] === candidate[name];
    });
  }

  function matchingContext(current, candidate) {
    try {
      var captured = new Date(candidate.captured_at);
      if (candidate.version !== 1 || candidate.platform !== "google" ||
          candidate.affiliate !== current.affiliate ||
          !sameIdentifiers(current.identifiers, candidate.identifiers) ||
          typeof candidate.captured_at !== "string" ||
          !Number.isFinite(captured.getTime()) || captured.getTime() > Date.now()) {
        return null;
      }
      // Preserve the original time for an unchanged, complete click tuple.
      current.captured_at = candidate.captured_at;
      return current;
    } catch (_error) {
      return null;
    }
  }

  function matchingStoredContext(current) {
    try {
      var raw = localStorage.getItem("thr_google_click_context");
      return raw ? matchingContext(current, JSON.parse(raw)) : null;
    } catch (_error) {
      return null;
    }
  }

  function currentGoogleTuple() {
    var params = new URLSearchParams(window.location.search);
    var affiliateValue = oneValue(params, "ref");
    if (affiliateValue === null) return null;
    var affiliate = affiliateValue.trim().toLowerCase();
    if (!/^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/.test(affiliate)) return null;
    var identifiers = {};
    var names = ["gclid", "gbraid", "wbraid"];
    for (var index = 0; index < names.length; index++) {
      var name = names[index];
      if (!params.has(name)) continue;
      var value = oneValue(params, name);
      if (!isValidGoogleClick(value)) return null;
      identifiers[name] = value;
    }
    if (Object.keys(identifiers).length === 0) return null;
    return { version: 1, platform: "google", affiliate: affiliate, identifiers: identifiers };
  }

  function storefrontRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  }

  function readMatchingCartContext(current) {
    return fetch(storefrontRoot() + "cart.js", {
      method: "GET",
      credentials: "same-origin"
    })
      .then(function (response) {
        if (!response.ok) throw new Error("cart.js failed with status " + response.status);
        return response.json();
      })
      .then(function (cart) {
        var raw = cart && cart.attributes && cart.attributes.thrivalist_google_click;
        if (!raw) return null;
        try {
          return matchingContext(current, JSON.parse(raw));
        } catch (_error) {
          return null;
        }
      });
  }

  function resolveContext(current) {
    // A recreated cart can use the convenience cache. When that cache is
    // blocked or was independently cleared, consult the server cart before
    // assigning a new time so an old URL reload cannot refresh its click age.
    var stored = matchingStoredContext(current);
    if (stored) return Promise.resolve(stored);
    return readMatchingCartContext(current).then(function (cartContext) {
      if (cartContext) return cartContext;
      current.captured_at = new Date().toISOString();
      return current;
    });
  }

  function enqueueCartMutation(operation) {
    // Serialize capture and clear writes. If consent changes while a capture is
    // in flight, the denial clear is therefore guaranteed to execute after the
    // capture instead of being overtaken by its response.
    var result = cartMutationQueue.then(operation, operation);
    cartMutationQueue = result.catch(function () {});
    return result;
  }

  function updateCartContext(context) {
    return enqueueCartMutation(function () {
      var attributes = {
        gclid: "", gbraid: "", wbraid: "",
        google_ads_user_data_consent: "GRANTED",
        thrivalist_google_click: JSON.stringify(context)
      };
      Object.keys(context.identifiers).forEach(function (name) {
        attributes[name] = context.identifiers[name];
      });
      return fetch(storefrontRoot() + "cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attributes: attributes }),
        credentials: "same-origin"
      })
        .then(function (response) {
          if (!response.ok) {
            throw new Error("cart/update.js failed with status " + response.status);
          }
          return response.json();
        });
    });
  }

  function startTracking() {
    if (captureInProgress) return;

    try {
      // Every authorization field comes from this URL. Never combine a new
      // visit with identifiers or ownership retained from an older visit.
      var current = currentGoogleTuple();
      if (!current) return;
      captureInProgress = true;

      resolveContext(current)
        .then(function (context) {
          if (!trackingIsAllowed()) return clearTracking();
          return updateCartContext(context).then(function () {
            if (!trackingIsAllowed()) return clearTracking();
            // Local storage is only a convenience cache. Server authorization
            // comes from the complete context written in one cart update.
            try {
              ["gclid", "gbraid", "wbraid"].forEach(function (name) {
                if (context.identifiers[name]) localStorage.setItem("thr_" + name, context.identifiers[name]);
                else localStorage.removeItem("thr_" + name);
              });
              localStorage.setItem("thr_google_click_context", JSON.stringify(context));
            } catch (_error) {
              console.error("Google click convenience storage unavailable");
            }
            console.log("Google click context saved to cart attributes");
          });
        })
        .catch(function (err) {
          console.error("Google click capture failed", err);
        })
        .then(function () {
          captureInProgress = false;
        });
    } catch (err) {
      captureInProgress = false;
      console.error("Google click capture script error", err);
    }
  }

  function clearTracking() {
    // Always retry the server clear on a denied page load. This also covers a
    // cart that still has context after browser storage was evicted or a prior
    // clear request failed.
    try {
      ["gclid", "gbraid", "wbraid"].forEach(function (name) {
        localStorage.removeItem("thr_" + name);
      });
      localStorage.removeItem("thr_google_click_context");
    } catch (_error) {
      // Local storage is optional; clearing the server-side cart is primary.
    }
    return enqueueCartMutation(function () {
      return fetch(storefrontRoot() + "cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          attributes: { gclid: "", gbraid: "", wbraid: "", google_ads_user_data_consent: "", thrivalist_google_click: "" }
        }),
        credentials: "same-origin"
      })
        .then(function (response) {
          if (!response.ok) throw new Error("cart context clear failed");
          return response.json();
        });
    }).catch(function () {
      console.error("Google click context clear failed");
    });
  }

  function trackingIsAllowed() {
    try {
      if (!window.Shopify || !window.Shopify.customerPrivacy) return false;
      var privacy = window.Shopify.customerPrivacy;
      if (typeof privacy.marketingAllowed === "function" ||
          typeof privacy.saleOfDataAllowed === "function") {
        return typeof privacy.marketingAllowed === "function" &&
          typeof privacy.saleOfDataAllowed === "function" &&
          Boolean(privacy.marketingAllowed()) &&
          Boolean(privacy.saleOfDataAllowed());
      }
      return typeof privacy.userCanBeTracked === "function" &&
        typeof privacy.userDataCanBeSold === "function" &&
        Boolean(privacy.userCanBeTracked()) &&
        Boolean(privacy.userDataCanBeSold());
    } catch (_error) {
      return false;
    }
  }

  function startIfAllowed() {
    if (trackingIsAllowed()) startTracking();
    else clearTracking();
  }

  function handleConsentChange() {
    if (trackingIsAllowed()) startTracking();
    else clearTracking();
  }

  try {
    if (window.Shopify && window.Shopify.loadFeatures && !window.Shopify.customerPrivacy) {
      window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], function (error) {
        if (error) clearTracking();
        else startIfAllowed();
      });
    } else {
      startIfAllowed();
    }
    document.addEventListener("visitorConsentCollected", handleConsentChange);
    // Retain compatibility with themes still using Shopify's historical
    // consent-tracking API while migrating to visitorConsentCollected.
    document.addEventListener("trackingConsentAccepted", startIfAllowed);
  } catch (err) {
    console.error("Google click capture script initialization error", err);
    try {
      clearTracking();
    } catch (_clearError) {
      // The initialization error is already recorded; remain fail closed.
    }
  }
})();
